/**
 * Storage abstraction. In Telegram WebView we'd ideally use the Mini App
 * CloudStorage API (per-user, synced across devices), but for the initial
 * scaffold localStorage is fine. Swap this implementation when CloudStorage
 * support is wired through @tma.js/sdk-react.
 */

export const storage = {
  get(key: string): string | null {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* ignore quota / privacy mode errors */
    }
  },
  remove(key: string): void {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* noop */
    }
  },
};

export const StorageKey = {
  Token: 'qlash.token',
  UserId: 'qlash.userId',
  SelectedGameId: 'qlash.selectedGameId',
  Appearance: 'qlash.appearance',
  Engagement: 'qlash.engagement',
  QgammonLandscape: 'qlash.qgammon_landscape',
  BannerCompact: 'qlash.banner_compact',
  OnboardingSeen: 'qlash.onboarding_seen',
  /** La lingua scelta nelle Preferenze (solo una scelta esplicita: `lib/lingua.ts`). */
  Lingua: 'qlash.lingua',
  /** La vibrazione (`settings.hapticsEnabled`): `'1'`/`'0'`, letta da `lib/hapticsPreference.ts`. */
  HapticsEnabled: 'qlash.haptics_enabled',
  /** Le emoji dell'avversario silenziate al tavolo (`'1'`/`'0'`): `lib/backgammon/preferenze.ts`. */
  QgammonSilenziaEmoji: 'qlash.qgammon_silenzia_emoji',
  /** Lo stile del lancio dei dadi di chi guarda (`'bounce'|'hand'|'trail'|'static'`): `lib/backgammon/preferenze.ts`. */
  QgammonDiceStyle: 'qlash.qgammon_dice_style',
  /** Il tiro automatico dei dadi (`'1'`/`'0'`, spento di default): `lib/backgammon/preferenze.ts`. */
  QgammonTiroAutomatico: 'qlash.qgammon_tiro_automatico',
  /** Dove l'utente ha lasciato il bottone dei minigiochi (`{"dx":n,"dy":n}`, UNA per tutto il guscio): `lib/minigiochiPosizione.ts`. */
  MinigiochiPosizione: 'qlash.minigiochi.posizione',
  // I tutorial NUOVI non aggiungono una chiave qui: usano `chiaveTutorialVisto(gioco)` (sotto), che rende la stessa forma `qlash.<gioco>.tutorialVisto`. Quelle che ci sono gia` restano come sono:
  // il «gia` visto» degli utenti non si azzera.
  /** Il tutorial del solitario e` stato aperto almeno una volta (`'1'`; `'0'` = non ancora, scritto alla prima lettura): `lib/solitario/useTutorialVisto.ts`, l'UNICO file della zona che la usa. */
  SolitarioTutorialVisto: 'qlash.solitario.tutorialVisto',
  /** Il tutorial della briscola e` stato aperto almeno una volta (`'1'`; `'0'` = non ancora, scritto alla prima lettura): `components/tutorial/IconaTutorial.tsx` via `lib/tutorial/useTutorialVisto.ts`. */
  BriscolaTutorialVisto: 'qlash.briscola.tutorialVisto',
  /** Il tutorial della scopa e` stato aperto almeno una volta (`'1'`; `'0'` = non ancora): lo scrive SOLO `lib/tutorial/useTutorialVisto.ts`, con la chiave che gli passa `components/scopa/IconaTutorialScopa.tsx`. */
  ScopaTutorialVisto: 'qlash.scopa.tutorialVisto',
  /** Il tutorial del tressette e` stato aperto almeno una volta (`'1'`; `'0'` = non ancora, scritto alla prima lettura): la legge e la scrive `lib/tutorial/useTutorialVisto.ts` (il «gia` visto» comune), la chiave la passa `components/tressette/IconaTutorialTressette.tsx`. */
  TressetteTutorialVisto: 'qlash.tressette.tutorialVisto',
  /** Il tutorial del sette e mezzo e` stato aperto almeno una volta (`'1'`; `'0'` = non ancora, scritto alla prima lettura): `lib/tutorial/useTutorialVisto.ts` (il «gia` visto» comune), la chiave la passa `HubSettemezzo`. */
  SettemezzoTutorialVisto: 'qlash.settemezzo.tutorialVisto',
} as const;

/** La chiave del «gia` visto» del tutorial di un gioco: `qlash.<gioco>.tutorialVisto`, le STESSE stringhe delle chiavi `<Gioco>TutorialVisto` di `StorageKey`. */
export type ChiaveTutorialVisto = `qlash.${string}.tutorialVisto`;

/** Il tutorial di un gioco nuovo prende la chiave da qui, senza una riga in `StorageKey` (che e` il punto dove due PR si toccavano). */
export const chiaveTutorialVisto = (gioco: string): ChiaveTutorialVisto => `qlash.${gioco}.tutorialVisto`;
